import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Info, ChevronDown } from "lucide-react";
import { Profile } from "@/hooks/useProfile";
import { useState } from "react";

interface DiscoverabilitySettingsProps {
  profile: Profile;
  onToggle: (isDiscoverable: boolean) => void;
  onLocationToggle: (locationVisible: boolean) => void;
}

export const DiscoverabilitySettings = ({ profile, onToggle, onLocationToggle }: DiscoverabilitySettingsProps) => {
  const [showPrivacyNote, setShowPrivacyNote] = useState(false);

  return (
    <Card className="border-0 shadow-none bg-gray-50">
      <CardContent className="p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="discoverable" className="text-sm font-medium">
                Make discoverable
              </Label>
              <p className="text-xs text-gray-500">
                Visible in Discover section
              </p>
            </div>
            <Switch
              id="discoverable"
              checked={profile.is_discoverable}
              onCheckedChange={onToggle}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="location-visible" className="text-sm font-medium">
                Show location
              </Label>
              <p className="text-xs text-gray-500">
                For location-based features
              </p>
            </div>
            <Switch
              id="location-visible"
              checked={profile.location_visible ?? true}
              onCheckedChange={onLocationToggle}
            />
          </div>
        </div>
        
        <Collapsible open={showPrivacyNote} onOpenChange={setShowPrivacyNote}>
          <CollapsibleTrigger className="flex items-center gap-2 text-xs text-blue-600 hover:text-blue-800">
            <Info className="w-3 h-3" />
            Privacy Details
            <ChevronDown className={`w-3 h-3 transition-transform ${showPrivacyNote ? 'rotate-180' : ''}`} />
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-800">
                <strong>Privacy Note:</strong> When discoverable, your profile will be visible to all users. 
                Hiding location may restrict broadcasting and location-based features.
              </p>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
};